<template>
  <a
    v-if="phone"
    class="whatsapp-chat"
    :href="chatUrl"
    target="_blank"
    rel="noopener noreferrer"
    :aria-label="label"
    :title="label"
    :style="floatingStyle"
  >
    <svg class="whatsapp-chat__icon" viewBox="0 0 32 32" aria-hidden="true">
      <path fill="currentColor" d="M16.04 3A12.89 12.89 0 0 0 5.02 22.58L3.3 28.87l6.43-1.69A12.96 12.96 0 1 0 16.04 3Zm0 23.58c-2.07 0-4.09-.56-5.85-1.61l-.42-.25-3.82 1 1.02-3.72-.27-.43A10.62 10.62 0 1 1 16.04 26.58Zm5.83-7.95c-.32-.16-1.89-.93-2.18-1.04-.29-.11-.5-.16-.72.16-.21.32-.82 1.04-1.01 1.25-.19.21-.37.24-.69.08-.32-.16-1.34-.49-2.55-1.58a9.56 9.56 0 0 1-1.77-2.2c-.19-.32-.02-.49.14-.65.14-.14.32-.37.48-.56.16-.18.21-.32.32-.53.11-.21.05-.4-.03-.56-.08-.16-.72-1.73-.98-2.37-.26-.62-.52-.54-.72-.55h-.61c-.21 0-.56.08-.85.4-.29.32-1.11 1.09-1.11 2.65 0 1.57 1.14 3.08 1.3 3.29.16.21 2.24 3.42 5.43 4.8.76.33 1.35.52 1.81.67.76.24 1.45.21 2 .13.61-.09 1.89-.77 2.16-1.52.27-.74.27-1.38.19-1.52-.08-.13-.29-.21-.61-.37Z"/>
    </svg>
    <span class="whatsapp-chat__label">{{ shortLabel }}</span>
  </a>
</template>

<script>
const COPY = {
  en: {
    label: 'Chat with us on WhatsApp at PineFitness',
    shortLabel: 'Chat with us',
    message: 'Hi PineFitness, I would like to learn more about your studio management software.'
  },
  el: {
    label: 'Μιλήστε μαζί μας στο WhatsApp στην PineFitness',
    shortLabel: 'Μιλήστε μαζί μας',
    message: 'Γεια σας PineFitness, θα ήθελα να μάθω περισσότερα για το λογισμικό διαχείρισης studio.'
  }
}

export default {
  name: 'WhatsAppChat',
  data() {
    return {
      footerOverlap: 0,
      positionFrame: null
    }
  },
  computed: {
    lang() {
      const path = this.$route && this.$route.path
      return path === '/el' || (path && path.indexOf('/el/') === 0) ? 'el' : 'en'
    },
    copy() {
      return COPY[this.lang]
    },
    phone() {
      const configured = process.env.VUE_APP_WHATSAPP_PHONE || process.env.VUE_APP_CONTACT_PHONE || '+357 99158398'
      return String(configured).replace(/\D/g, '')
    },
    chatUrl() {
      return `https://wa.me/${this.phone}?text=${encodeURIComponent(this.copy.message)}`
    },
    label() {
      return this.copy.label
    },
    shortLabel() {
      return this.copy.shortLabel
    },
    floatingStyle() {
      return { '--whatsapp-footer-overlap': `${this.footerOverlap}px` }
    }
  },
  mounted() {
    this.updateFooterOverlap()
    window.addEventListener('scroll', this.schedulePositionUpdate, { passive: true })
    window.addEventListener('resize', this.schedulePositionUpdate, { passive: true })
  },
  beforeUnmount() {
    window.removeEventListener('scroll', this.schedulePositionUpdate)
    window.removeEventListener('resize', this.schedulePositionUpdate)
    if (this.positionFrame) window.cancelAnimationFrame(this.positionFrame)
  },
  methods: {
    schedulePositionUpdate() {
      if (this.positionFrame) return
      this.positionFrame = window.requestAnimationFrame(() => {
        this.positionFrame = null
        this.updateFooterOverlap()
      })
    },
    updateFooterOverlap() {
      const footer = document.querySelector('footer')
      if (!footer) {
        this.footerOverlap = 0
        return
      }

      const rect = footer.getBoundingClientRect()
      this.footerOverlap = rect.bottom > 0
        ? Math.max(0, Math.round(window.innerHeight - rect.top))
        : 0
    }
  }
}
</script>

<style scoped>
.whatsapp-chat {
  position: fixed;
  right: 22px;
  bottom: calc(22px + var(--whatsapp-footer-overlap, 0px));
  z-index: 1015;
  display: inline-flex;
  align-items: center;
  gap: 9px;
  min-height: 54px;
  padding: 10px 17px 10px 13px;
  border-radius: 999px;
  color: #fff;
  background: #087b44;
  box-shadow: 0 12px 32px rgba(8, 47, 27, 0.24);
  font-weight: 700;
  text-decoration: none;
  transition: bottom 0.12s linear, transform 0.18s ease, box-shadow 0.18s ease, background 0.18s ease;
}

.whatsapp-chat:hover,
.whatsapp-chat:focus-visible {
  color: #fff;
  background: #066a39;
  box-shadow: 0 16px 38px rgba(8, 47, 27, 0.3);
  transform: translateY(-2px);
}

.whatsapp-chat:focus-visible {
  outline: 3px solid rgba(37, 211, 102, 0.3);
  outline-offset: 3px;
}

.whatsapp-chat__icon {
  width: 30px;
  height: 30px;
  flex: 0 0 auto;
}

.whatsapp-chat__label {
  white-space: nowrap;
}

@media (max-width: 575px) {
  .whatsapp-chat {
    right: 16px;
    bottom: calc(16px + var(--whatsapp-footer-overlap, 0px));
    width: 56px;
    height: 56px;
    min-height: 0;
    justify-content: center;
    padding: 0;
  }

  .whatsapp-chat__label {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .whatsapp-chat {
    transition: none;
  }
}
</style>
